import { Component, output, input, computed, ChangeDetectionStrategy } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { TabsModule } from '@openng/optimus-ui/tabs';
import { DatePickerModule } from '@openng/optimus-ui/datepicker';
import { PeriodOption } from '../../models/dashboard.types';

export interface DateRange {
  startDate: Date;
  endDate: Date;
}

@Component({
  selector: 'app-date-range-selector',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, TabsModule, DatePickerModule],
  template: `
    <div class="selector-wrapper">
      <div class="period-selector-container">
        <p-tabs [value]="selectedPeriod()" (valueChange)="onPeriodChange($event)">
          <p-tablist>
            @for (period of periodOptionsFiltered(); track period.value) {
              <p-tab [value]="period.value">{{ period.label }}</p-tab>
            }
          </p-tablist>
        </p-tabs>
      </div>
      @if (selectedPeriod() === 'Custom') {
        <div class="custom-period-selector">
          <p-datepicker
            [ngModel]="startDate()"
            (ngModelChange)="onStartDateChange($event)"
            placeholder="Start Date"
            [firstDayOfWeek]="1"
            styleClass="p-inputtext-sm"
            [maxDate]="effectiveEndDate()"
          />
          <p-datepicker
            [ngModel]="effectiveEndDate()"
            (ngModelChange)="onEndDateChange($event)"
            [firstDayOfWeek]="1"
            styleClass="p-inputtext-sm"
            [minDate]="startDate()"
            [maxDate]="today"
          />
        </div>
      }
    </div>
  `,
  styles: [
    `
      .selector-wrapper {
        display: flex;
        align-items: center;
        gap: 1rem;
        flex-wrap: wrap;
      }

      .custom-period-selector {
        display: flex;
        gap: 0.5rem;
        align-items: center;
      }
    `,
  ],
})
export class DateRangeSelectorComponent {
  readonly includeWeek = input<boolean>(true);
  readonly selectedPeriod = input<PeriodOption>('Month');
  readonly startDate = input<Date | null>(null);
  readonly endDate = input<Date | null>(null);

  readonly periodChange = output<PeriodOption>();
  readonly dateRangeChange = output<DateRange>();

  // Today at end of day for maxDate comparison
  protected readonly today = (() => {
    const d = new Date();
    d.setHours(23, 59, 59, 999);
    return d;
  })();

  // Today at start of day for default display value
  protected readonly todayStart = (() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  })();

  // Computed value that defaults to today when endDate is null
  protected readonly effectiveEndDate = computed(() => this.endDate() ?? this.todayStart);

  private readonly allPeriodOptions: { label: string; value: PeriodOption }[] = [
    { label: 'This Week', value: 'Week' },
    { label: 'This Month', value: 'Month' },
    { label: 'This Year', value: 'Year' },
    { label: 'Custom', value: 'Custom' },
  ];

  protected readonly periodOptionsFiltered = computed(() => {
    if (this.includeWeek()) {
      return this.allPeriodOptions;
    }
    return this.allPeriodOptions.filter((p) => p.value !== 'Week');
  });

  onPeriodChange(period: unknown): void {
    const periodValue = period as PeriodOption;
    this.periodChange.emit(periodValue);

    if (periodValue === 'Custom') {
      // When switching to Custom, emit with current dates (endDate defaults to today)
      const start = this.startDate();
      if (start) {
        const dateRange = this.createDateRange(start, this.effectiveEndDate());
        this.dateRangeChange.emit(dateRange);
      }
    } else {
      const dateRange = this.calculatePeriodDates(periodValue);
      this.dateRangeChange.emit(dateRange);
    }
  }

  onStartDateChange(date: Date | null): void {
    if (date) {
      // Default endDate to today if not set
      const dateRange = this.createDateRange(date, this.effectiveEndDate());
      this.dateRangeChange.emit(dateRange);
    }
  }

  onEndDateChange(date: Date | null): void {
    if (date && this.startDate()) {
      const dateRange = this.createDateRange(this.startDate()!, date);
      this.dateRangeChange.emit(dateRange);
    }
  }

  private calculatePeriodDates(period: PeriodOption): DateRange {
    const now = new Date();
    let startDate: Date;
    const endDate = new Date();

    switch (period) {
      case 'Week': {
        const dayOfWeek = now.getDay();
        const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - diff);
        break;
      }
      case 'Month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'Year':
        startDate = new Date(now.getFullYear(), 0, 1);
        break;
      default:
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
    }

    return this.createDateRange(startDate, endDate);
  }

  private createDateRange(start: Date, end: Date): DateRange {
    const startDate = new Date(start);
    const endDate = new Date(end);

    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(23, 59, 59, 999);

    return { startDate, endDate };
  }

  getDateRange(): DateRange {
    if (this.selectedPeriod() === 'Custom') {
      const start = this.startDate() || new Date();
      const end = this.endDate() || new Date();
      return this.createDateRange(start, end);
    }
    return this.calculatePeriodDates(this.selectedPeriod());
  }
}
